import { ArrowSquareOutIcon } from "@phosphor-icons/react/dist/csr/ArrowSquareOut";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { FieldWithAction } from "@/accounts-payable/common/field-with-action";
import { quickbooksBillNumber } from "@/accounts-payable/quickbooks-bill";
import type { ApInvoice, ApQuickbooksBill } from "@/accounts-payable/types";
import { QuickbooksBillPushButton } from "./quickbooks-bill-push-button";
import { QuickbooksBillPushNote } from "./quickbooks-bill-push-note";
import { useQuickbooksBillPush } from "./use-quickbooks-bill-push";

export const QuickbooksBillField = ({
  invoice,
  businessId,
}: {
  invoice: Pick<ApInvoice, "id" | "invoice_number" | "quickbooks_bill">;
  businessId: string | null;
}) => {
  const { ready, isError } = useQuickbooksBillPush(invoice, businessId);
  if (!invoice.quickbooks_bill && !ready && !isError) return null;

  return (
    <FieldWithAction
      label="QuickBooks bill"
      action={
        <QuickbooksBillPushButton
          invoice={invoice}
          businessId={businessId}
          appearance="link"
        />
      }
    >
      <Stack className="w-full" align="start" gap={1}>
        {invoice.quickbooks_bill ? (
          <BillValue
            bill={invoice.quickbooks_bill}
            number={quickbooksBillNumber(invoice.invoice_number)}
          />
        ) : (
          <Text tone="muted">Not in QuickBooks yet</Text>
        )}
        <QuickbooksBillPushNote invoice={invoice} businessId={businessId} />
      </Stack>
    </FieldWithAction>
  );
};

const BILL_STATUS_NOTE: Record<ApQuickbooksBill["status"], string | null> = {
  open: null,
  paid: "paid",
  deleted: "deleted in QuickBooks",
};

const BillValue = ({
  bill,
  number,
}: {
  bill: ApQuickbooksBill;
  number: string | null;
}) => {
  const label = number ? `Bill #${number}` : "Bill";
  const url = bill.status !== "deleted" ? bill.url : null;
  const notes = [
    BILL_STATUS_NOTE[bill.status],
    bill.linked_existing && "linked to an existing bill",
  ].filter(Boolean);

  return (
    <Stack direction="row" gap={2} align="center" className="flex-wrap">
      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Open ${number ? `bill #${number}` : "the bill"} in QuickBooks`}
          className="inline-flex items-center gap-1.5 rounded-sm text-lg text-link hover:underline focus-visible:ring-2 focus-visible:ring-focus focus-visible:outline-hidden"
        >
          {label}
          <ArrowSquareOutIcon className="h-3 w-3" aria-hidden />
        </a>
      ) : (
        <Text>{label}</Text>
      )}
      {notes.length > 0 && <Text tone="muted">({notes.join(", ")})</Text>}
    </Stack>
  );
};
